// Block registry — per-page sections inside the Barba container.
//
// Scans a container for [data-block="name"], lazy-loads the matching
// module from resources/js/blocks/<name>.js (each becomes its own webpack
// chunk, fetched only when the section exists on the page), instantiates
// it and drives the lifecycle. scroll() is deferred until the section is
// within one viewport of the screen.
const active = [];

const observer = new IntersectionObserver(
	(entries) => {
		entries.forEach((entry) => {
			if (!entry.isIntersecting) return;

			observer.unobserve(entry.target);
			const block = active.find((b) => b.el === entry.target);
			if (block) block.scroll();
		});
	},
	{ rootMargin: '100% 0px' }
);

// "Above the fold" is measured relative to the block's page container, not
// the window: during a Barba transition both the outgoing and incoming
// containers are briefly in the DOM, which would offset window-based
// measurements. Distance from the container top is stable in both states
// (the page is always scrolled to top before the new container enters).
function isAboveFold(el, container) {
	const root = container.getBoundingClientRect ? container : document.documentElement;
	const offset = el.getBoundingClientRect().top - root.getBoundingClientRect().top;

	// 0.88 mirrors ST_DEFAULTS start 'top 88%' in lib/animations.js: a block
	// counts as above-fold only if it starts above the line where the magic*()
	// ScrollTriggers fire. A block sitting exactly at the fold (e.g. right
	// after a 100svh hero) must be below-fold, or subpixel rounding parks it
	// in a dead zone — enter() intro invisible, scroll() reveal skipped.
	return offset < window.innerHeight * 0.88;
}

// Mount blocks: load chunks, instantiate and prepare() immediately (the
// initial hidden states must exist BEFORE the page becomes visible); run
// enter() and begin observing right away — or, with { defer: true },
// return a starter so the caller plays the intros once the page is
// actually visible (transitions.js starts the incoming page's blocks only
// after the slide settles, so intros aren't wasted offscreen).
//
// inViewport is measured once, at mount — container-relative, so it is
// stable mid-transition — and prepare()/enter() gate on the same value.
export async function mountBlocks(container, { defer = false } = {}) {
	const nodes = [...container.querySelectorAll('[data-block]')];
	const mounted = [];

	await Promise.all(
		nodes.map(async (el) => {
			const name = el.dataset.block;

			let module;
			try {
				module = await import(
					/* webpackChunkName: "block-[request]" */
					/* webpackExclude: /_template/ */
					`../blocks/${name}`
				);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.warn(`[blocks] no module for data-block="${name}"`, error);
				return;
			}

			const block = new module.default(el);
			block.inViewport = isAboveFold(el, container);
			active.push(block);
			mounted.push(block);
			block.prepare();
		})
	);

	const start = () => {
		mounted.forEach((block) => {
			// Unmounted in the meantime (e.g. rapid re-navigation) — skip.
			if (!active.includes(block)) return;

			block.enter();
			observer.observe(block.el);
		});
	};

	if (defer) return start;

	start();
	return null;
}

// Unmount blocks — all of them, or only those inside `container` (needed
// for sync transitions where outgoing and incoming blocks coexist).
export function unmountBlocks(container = null) {
	for (let i = active.length - 1; i >= 0; i--) {
		const block = active[i];
		if (container && !container.contains(block.el)) continue;

		observer.unobserve(block.el);
		block.exit();
		block.destroy();
		active.splice(i, 1);
	}
}

// Find a mounted block by name (first match) — for cross-block access.
export function getBlock(name) {
	return active.find((b) => b.el.dataset.block === name) || null;
}
